import { Sparkles } from 'lucide-react';
import { useState } from 'react';

import { api } from '@/lib/api';
import {
  ICON_CATEGORIES,
  findLibraryIcon,
  searchIcons,
  type IconCategory,
} from '@/lib/icon-library';
import { cn } from '@/lib/utils';

import { Modal } from '../ui/Modal';

interface Props {
  open: boolean;
  onClose: () => void;
  value?: string;
  onPick: (id: string) => void;
  /** Optional context fed to the AI-suggest tab (slide/step description). */
  aiContext?: string;
}

export function IconPicker({ open, onClose, value, onPick, aiContext }: Props) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<IconCategory | null>(null);
  const [suggested, setSuggested] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);

  const results = searchIcons(query, category ?? undefined);

  const runSuggest = async () => {
    if (!aiContext?.trim()) return;
    setLoading(true);
    try {
      const res = await api.ai.suggestIcons({ context: aiContext, max: 8 });
      setSuggested(res.suggestions.map((s) => s.id));
    } catch (err) {
      console.warn('AI icon suggestions failed — showing none', err);
      setSuggested([]);
    } finally {
      setLoading(false);
    }
  };

  const pick = (id: string) => {
    onPick(id);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Choose an icon" width="lg">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search icons…"
            className="h-9 flex-1 rounded-md border border-border bg-white px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          />
          {aiContext?.trim() ? (
            <button
              onClick={runSuggest}
              disabled={loading}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-[13px] font-medium text-fg hover:bg-bg-subtle disabled:opacity-50"
            >
              <Sparkles size={13} /> {loading ? 'Thinking…' : 'AI suggest'}
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Chip active={category === null} onClick={() => setCategory(null)}>
            All
          </Chip>
          {ICON_CATEGORIES.map((c) => (
            <Chip key={c.id} active={category === c.id} onClick={() => setCategory(c.id)}>
              {c.label}
            </Chip>
          ))}
        </div>

        {suggested && suggested.length > 0 ? (
          <div>
            <div className="mb-1.5 text-xs font-semibold text-fg-subtle">AI suggestions</div>
            <IconGrid ids={suggested} value={value} onPick={pick} />
          </div>
        ) : null}

        <IconGrid ids={results.map((r) => r.id)} value={value} onPick={pick} />
      </div>
    </Modal>
  );
}

function IconGrid({
  ids,
  value,
  onPick,
}: {
  ids: string[];
  value?: string;
  onPick: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-6 gap-2 sm:grid-cols-8">
      {ids.map((id) => {
        const { Glyph, name } = findLibraryIcon(id);
        return (
          <button
            key={id}
            title={name}
            onClick={() => onPick(id)}
            className={cn(
              'flex aspect-square flex-col items-center justify-center gap-1 rounded-md border p-1 text-fg-muted transition hover:bg-bg-subtle hover:text-fg',
              value === id ? 'border-accent bg-accent-soft/40 text-accent' : 'border-border',
            )}
          >
            <Glyph size={22} />
            <span className="line-clamp-1 text-[9px] leading-tight">{name}</span>
          </button>
        );
      })}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-[11px] font-medium transition',
        active ? 'border-accent bg-accent text-white' : 'border-border text-fg-muted hover:bg-bg-subtle',
      )}
    >
      {children}
    </button>
  );
}
